"use client";

// Step 8.1.2 hotfix / PASTE #363: client-side admin check that drives optional UI
// rendering (the Admin link in TopBar).
//
// PASTE #363 (BUG A): this used to read the access token from the client Amplify
// session. After the MKT-#10 code split, Amplify.configure runs from a
// dynamically-imported chunk that may not have executed when this hook mounts -
// so the session read threw for a perfectly valid admin session and the hook set
// isAdmin=false, hiding the Admin tab (most visibly after an impersonation exit,
// which hard-reloads). Now it reads the same server-validated mag-portal-access
// cookie the middleware trusts - exactly like useIsSignedIn / useIsApproved
// (MKT-#10) - so there is no configure race. The server requireAdmin remains the
// cryptographic gate; this is a UI affordance only.

import { useEffect, useState } from "react";
import { isAdminFromAccessToken } from "@/lib/auth/groups";

const ACCESS_COOKIE = "mag-portal-access";

function readAccessCookie(): string | null {
  if (typeof document === "undefined") return null;
  for (const raw of document.cookie.split(";")) {
    const trimmed = raw.trim();
    if (trimmed.startsWith(`${ACCESS_COOKIE}=`)) {
      return decodeURIComponent(trimmed.slice(ACCESS_COOKIE.length + 1));
    }
  }
  return null;
}

export function useIsAdmin(): boolean {
  const [isAdmin, setIsAdmin] = useState<boolean>(false);

  useEffect(() => {
    let cancelled = false;
    const recheck = () => {
      if (cancelled) return;
      setIsAdmin(isAdminFromAccessToken(readAccessCookie()));
    };
    recheck();
    // Re-read when the tab regains focus / becomes visible (covers a sign-in,
    // sign-out, or impersonation exit that happened via navigation).
    window.addEventListener("focus", recheck);
    document.addEventListener("visibilitychange", recheck);
    return () => {
      cancelled = true;
      window.removeEventListener("focus", recheck);
      document.removeEventListener("visibilitychange", recheck);
    };
  }, []);

  return isAdmin;
}
